/**
 * This component is the basis for discover
 * and collection library as they are
 * substantially the same
 */
import React, { Component, ReactNode, useEffect, useRef } from 'react';
import { classes, stylesheet } from 'typestyle';
import { loadTranslation } from '@src/services/helpers/appHelpers';
import usePrevious from '@src/hooks/Media/usePrevious';
import { Loader } from '../Loader';
import { InfiniteScroller, InfiniteScrollerHandle, Props as InfiniteScrollerProps } from '../InfiniteScroller';
import { PerfectScrollbar, PerfectScrollbarHandle } from '../PerfectScrollbar';
import CustomMasonry, { CustomMasonryProps } from '@src/components/Library/CustomMasonry';

interface Props {
  libraryLoaded: boolean
  noMore: boolean
  loadMore: InfiniteScrollerProps['load']
  elements: LibElement[] | null
  onScroll?: (scrollTop: number) => void
}

export interface LibElement {
  node: ReactNode
  dimensions: {
    width: number
    height: number
  } | undefined
}

/**
 * Organize given element to to display them in a grid layout like
 * the masonry plugin
 */
export default function GridLibraryLayout({
  libraryLoaded,
  loadMore,
  noMore,
  elements,
  onScroll,
}: Props) {
  const masonryRef = useRef<Component<CustomMasonryProps>>(null);
  const scrollbarRef = useRef<PerfectScrollbarHandle>(null);
  const infiniteScrollbarRef = useRef<InfiniteScrollerHandle>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const previousElements = usePrevious(elements);

  useEffect(() => {
    // When we have less element than previously, a reload has been performed
    if ((elements?.length || 0) < (previousElements?.length || 0)) {
      scrollbarRef.current?.scrollTop(0);
      infiniteScrollbarRef.current?.resetPage();
    }
  }, [elements]);

  // Update PerfectScrollbar when elements change or library loads
  useEffect(() => {
    if (libraryLoaded && elements && elements.length > 0) {
      // Delay to allow masonry to finish layout
      const timer = setTimeout(() => {
        scrollbarRef.current?.update();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [elements?.length, libraryLoaded]);

  // Use ResizeObserver to update PerfectScrollbar when grid content resizes
  useEffect(() => {
    const gridContainer = gridContainerRef.current;
    if (!gridContainer) return;

    const resizeObserver = new ResizeObserver(() => {
      scrollbarRef.current?.update();
    });

    resizeObserver.observe(gridContainer);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <>
      {!libraryLoaded ? (
        <div className={sheet.loaderContainer}>
          <Loader />
        </div>
      ) : null}
      <InfiniteScroller
        ref={infiniteScrollbarRef}
        container={(props, children) => {
          const { onScroll: propsOnScroll, ...restProps } = props;
          return (
            <PerfectScrollbar
              ref={scrollbarRef}
              className={sheet.library}
              options={{ minScrollbarLength: 30 }}
              onScroll={(e) => {
                if (onScroll) {
                  const target = e.target as HTMLElement;
                  onScroll(target.scrollTop);
                }
                if (propsOnScroll) {
                  propsOnScroll(e as any);
                }
              }}
              {...restProps}
            >
              {children}
            </PerfectScrollbar>
          );
        }}
        noMore={noMore}
        threshold={1700}
        loader={libraryLoaded} // Doesn't display the infinite loader if the library is reloading
        load={loadMore}
      >
        <div className={sheet.scrollContent}>
          <div
            ref={gridContainerRef}
            className={classes(sheet.gridContainer, {
              [sheet.hideGridContainer]: !libraryLoaded,
            })}
          >
            <CustomMasonry
              ref={masonryRef}
              updateOnEachImageLoad
              options={{
                transitionDuration: 0,
                initLayout: false,
              }}
              onImagesLoaded={masonryImagesLoaded}
            >
              {elements?.map(({ node }) => node)}
            </CustomMasonry>
          </div>
          {/* Bottom section: show "End of results" when noMore, otherwise just spacer for scroll room */}
          {noMore && elements && elements.length > 0 && libraryLoaded ? (
            <div className={sheet.endOfResults}>
              {lang.endOfResults}
            </div>
          ) : (
            <div className={sheet.bottomSpacer} />
          )}
          {(elements === null || elements.length === 0) && libraryLoaded ? (
            <div className={sheet.nothing}>
              {lang.nothing}
            </div>
          ) : null}
        </div>
      </InfiniteScroller>
    </>
  );

  function masonryImagesLoaded() {
    setTimeout(() => {
      // performLayout() is a react-masonry-component method not exposed in its typings
      (masonryRef.current as any)?.performLayout(); // Fix firefox bug
      // Update PerfectScrollbar after masonry layout to fix scroll height
      scrollbarRef.current?.update();
    }, 200);
  }
}

const sheet = stylesheet({
  loaderContainer: {
    position: 'relative',
    textAlign: 'center',
    marginTop: 125,
  },
  library: {
    position: 'relative',
    paddingRight: 10,
    height: '100%',
    overflow: 'hidden',
  },
  scrollContent: {
    // Single wrapper for all scrollable content
  },
  gridContainer: {
    marginTop: 2,
  },
  hideGridContainer: {
    display: 'none',
  },
  bottomSpacer: {
    height: 300,
    width: '100%',
    flexShrink: 0,
  },
  endOfResults: {
    height: 300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'gray',
    fontSize: 13,
  },
  column: {
    display: 'inline-block',
    verticalAlign: 'top',
  },
  nothing: {
    textAlign: 'center',
    paddingTop: 150,
    color: 'gray',
  },
});

const translation = {
  en: {
    nothing: 'No content to display',
    endOfResults: 'End of results',
  },
};
const lang = loadTranslation(translation);
